import React, { useEffect, useState } from 'react';
import { getPremiumLinks } from '../api/pocketbase';

// 1. Type Definitions based on your Schema
export interface PremiumLink {
  id: string;
  url: string;
  website: 'baotran.de' | 'cotuong-online.com' | string; // Based on your select values
  description: string;
  preview: string; // URL to image
  created: string;
  updated: string;
}

interface PremiumLinksFeedProps {

}

// Helper to assign specific colors to specific websites
const getBadgeColor = (website: string) => {
  switch (website) {
    case 'baotran.de':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'cotuong-online.com':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const PremiumLinkCard: React.FC<{ item: PremiumLink }> = ({ item }) => {
  return (
    <div className='group flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden'>
      {/* Preview Image Section */}
      <a
        href={item.url}
        target='_blank'
        rel='noopener noreferrer'
        className='relative block h-48 overflow-hidden bg-gray-100'
      >
        {item.preview ? (
          <img
            src={item.preview}
            alt={item.description || 'Link preview'}
            className='w-full h-full object-cover transition-transform duration-500 group-hover:scale-105'
            loading='lazy'
          />
        ) : (
          <div className='flex items-center justify-center h-full text-gray-400'>
            <svg
              className='w-12 h-12'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          </div>
        )}

        {/* Website Badge (Overlay) */}
        {item.website && (
          <span
            className={`absolute top-3 right-3 px-2.5 py-1 text-xs font-semibold rounded-full border shadow-sm ${getBadgeColor(item.website)}`}
          >
            {item.website}
          </span>
        )}
      </a>

      {/* Content Section */}
      <div className='flex flex-col flex-grow p-5'>
        <h3 className='font-semibold text-lg text-gray-900 leading-tight mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors'>
          <a href={item.url} target='_blank' rel='noopener noreferrer'>
            {item.description || 'Untitled Link'}
          </a>
        </h3>

        {/* URL Display (Truncated) */}
        <p className='text-sm text-gray-500 mb-4 truncate font-mono'>
          {item.url}
        </p>

        {/* Footer / Meta */}
        <div className='mt-auto pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400'>
          <span>{new Date(item.created).toLocaleDateString()}</span>

          <a
            href={item.url}
            target='_blank'
            rel='noopener noreferrer'
            className='flex items-center gap-1 font-medium text-blue-600 hover:underline'
          >
            Visit
            <svg
              className='w-3 h-3'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M14 5l7 7m0 0l-7 7m7-7H3'
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

export const PremiumLinksFeed: React.FC<PremiumLinksFeedProps> = ({}) => {
  const [links, setLinks] = useState<PremiumLink[]>([]);

  useEffect(() => {
    const getLinks = async () => {
      const linkz = await getPremiumLinks();
      setLinks(linkz);
    };
    getLinks();
  }, []);

  if (!links || links.length === 0) {
    return (
      <div className='text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300'>
        <p className='text-gray-500'>No premium links found.</p>
      </div>
    );
  }

  return (
    <section className='py-8'>
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-2xl font-bold text-gray-800'>Premium Collection</h2>
      </div>

      {/* Responsive Grid: 1 col mobile, 2 col tablet, 3 col desktop */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
        {links.map((link) => (
          <PremiumLinkCard key={link.id} item={link} />
        ))}
      </div>
    </section>
  );
};
